import {useTranslation} from "react-i18next";
import {useRecoilState, useRecoilValue} from "recoil";
import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {Button, Chip, Stack, Tooltip, useTheme} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import IconButton from "@mui/material/IconButton";
import {DescriptionRounded, LibraryAddRounded, WarningRounded} from "@mui/icons-material";
import {FilterDef} from "../../../constants/FinanceModels";
import EmptyState from "../../../../../util/component/EmptyState";
import {getLocalGridState, setLocalGridState} from "../../../utils/GridUtils";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import {STATE_REGISTRY_FUEL_ACTIVE_FILTERS, STATE_REGISTRY_FUEL_TABLE_UPDATING} from "../constants/FuelCardRecoil";
import dayjs from "dayjs";
import DeleteIcon from "@mui/icons-material/Delete";
import FinanceDialog from "../../../component/FinanceDialog";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {useHasScope} from "../../../../../config/globalConfig";
import {
    SCOPE_REGISTRY_FUEL_CARD_ADD,
    SCOPE_REGISTRY_FUEL_CARD_DELETE,
    SCOPE_REGISTRY_FUEL_CARD_EDIT,
    SCOPE_REGISTRY_FUEL_CARD_VIEW_DETAIL
} from "../constants/RegistryFuelCardAuth";
import {useRegistryFuelCardsRepository} from "../../../api/repositories/RegistryFuelCardsRepository";
import EditIcon from "@mui/icons-material/Edit";
import RegistryFuelCardDetail from "../pages/RegistryFuelCardDetail";
import {useRegistryFuelCardStatuses} from "../constants/useRegistryFuelCardStatuses";
import {useRegistryFuelCardUse} from "../constants/useRegistryFuelCardUse";
import {useRegistryFuelCardProviders} from "../constants/useRegistryFuelCardProviders";

const RegistryFuelCardsTable = (props: any) => {

    const UNIQUE_ID = "fnn-registry-fuel-cards";

    const {t} = useTranslation("finance", {keyPrefix: "registryFuelCard"});
    const {getRegistryFuelCards, deleteRegistryFuelCard} = useRegistryFuelCardsRepository();
    const [updating, setUpdating] = useRecoilState(STATE_REGISTRY_FUEL_TABLE_UPDATING);
    const filterDefs = useRecoilValue(STATE_REGISTRY_FUEL_ACTIVE_FILTERS);
    const navigate = useNavigate();
    const [registryFuelCard, setRegistryFuelCard] = useState(false);
    const [openDetailModal, setOpenDetailModal] = useState(false);
    const [isReadonly, setIsReadonlyDetail] = useState(false);
    const [copyRegistryCard, setCopyRegistryCard] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [rowId, setRowId] = useState<string>("");
    const [cardNumber, setCardNumber] = useState<string>("");
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const gridRef = useRef<AgGridReact>(null);
    const theme = useTheme();
    const hasScope = useHasScope;
    const {getRegistryFuelCardStatusColor, getRegistryFuelCardStatuses} = useRegistryFuelCardStatuses();
    const {getRegistryFuelCardUse} = useRegistryFuelCardUse();
    const {getRegistryFuelCardProviders} = useRegistryFuelCardProviders();

    const columnDefs : ColDef[] = [
        {
            field: "cardNumber", headerName: t("tables.cardNumber"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value
        },
        {
            field: "provider", headerName: t("tables.provider"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value ? getRegistryFuelCardProviders(params.value) : ""
        },
        {
            field: "msAssignee", headerName: t("tables.msAssignee"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value
        },
        {
            field: "idMsAssignee", headerName: t("tables.idMsAssignee"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value
        },
        {
            field: "dueDate", headerName: t('tables.dueDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true,
        },
        {
            field: "cardUse", headerName: t("tables.cardUse"),
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => params.value ? getRegistryFuelCardUse(params.value) : ""
        },
        {
            field: "legalEntity", headerName: t("tables.legalEntity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
        },
        {
            field: "dealer", headerName: t("tables.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
        },
        {
            field: "status", headerName: t("tables.status"),
            minWidth: 150,
            sortable: true, unSortIcon: true,
            lockPosition: "right", lockVisible: true, lockPinned: true, pinned: "right",
            cellRenderer: (params: any) => {
                return <Tooltip title={params.data.errorMessage ? t('messages.' + params.data.errorMessage) : ""}>
                    <Chip label={getRegistryFuelCardStatuses(params.value)}
                          color={getRegistryFuelCardStatusColor(params.value)}/>
                </Tooltip>
            }
        },
        {
            field: "_actions", headerName: '',
            minWidth: 100, maxWidth: 110,
            unSortIcon: false, sortable: false,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () => (
                        <FinanceDynamicActions>
                            { hasScope(SCOPE_REGISTRY_FUEL_CARD_VIEW_DETAIL) &&
                                <Tooltip arrow title={t('tables.tooltips.showDetail')}>
                                    <IconButton
                                                sx={{color: theme.palette.accent.main}}
                                                onClick={() => {
                                                    setRegistryFuelCard(params.data);
                                                    setOpenDetailModal(true);
                                                    setIsReadonlyDetail(true);
                                                    setCopyRegistryCard(false);
                                                }}
                                    >
                                        <DescriptionRounded/>
                                    </IconButton>
                                </Tooltip>
                            }
                            { hasScope(SCOPE_REGISTRY_FUEL_CARD_EDIT) &&
                                <Tooltip arrow title={t('tables.tooltips.edit')}>
                                    <IconButton
                                                sx={{color: theme.palette.accent.main}}
                                                onClick={() => {
                                                    setRegistryFuelCard(params.data);
                                                    setOpenDetailModal(true);
                                                    setIsReadonlyDetail(false);
                                                    setCopyRegistryCard(false);
                                                }}
                                    >
                                        <EditIcon/>
                                    </IconButton>
                                </Tooltip>
                            }
                            { hasScope(SCOPE_REGISTRY_FUEL_CARD_ADD) &&
                                <Tooltip arrow title={t('tables.tooltips.copy')}>
                                    <IconButton
                                                sx={{color: theme.palette.accent.main}}
                                                onClick={() => {
                                                    setRegistryFuelCard(params.data);
                                                    setOpenDetailModal(true);
                                                    setIsReadonlyDetail(false);
                                                    setCopyRegistryCard(true);
                                                }}
                                    >
                                        <LibraryAddRounded/>
                                    </IconButton>
                                </Tooltip>
                            }
                            { hasScope(SCOPE_REGISTRY_FUEL_CARD_DELETE) &&
                                <Tooltip arrow title={t('tables.tooltips.delete')}>
                                    <IconButton
                                        sx={{color: theme.palette.accent.main}}
                                        onClick={() => {
                                            setRowId(params.data.id);
                                            setCardNumber(params.data.cardNumber)
                                            setRegistryFuelCard(params.data);
                                            setOpenDeleteDialog(true);
                                        }}
                                    >
                                        <DeleteIcon/>
                                    </IconButton>
                                </Tooltip>
                            }
                        </FinanceDynamicActions>
                    ),
                };
            },
            suppressFiltersToolPanel: true,
        },
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {
            title: t('tables.noRowsOverlay.title'),
            content: t("tables.noRowsOverlay.content")
        }
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
    }, [filterDefs]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
        }
    }, [updating]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                    params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                getRegistryFuelCards(startRow, endRow - startRow, sort, order, filterDefs)
                    .then((response) => {
                        let result = response.result;
                        if (result.total === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result.total,
                        });
                    })
                    .catch(() => params.fail());
            },
        };
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    const toolbar = {
        renderActions: () => (
            <Stack>
                {hasScope(SCOPE_REGISTRY_FUEL_CARD_ADD) &&
                    <Button fullWidth color={"accent"} variant="contained" component="label"
                         startIcon={<LibraryAddRounded/>}
                         onClick={() => navigate("/finance/registryFuelCards/add")}
                    > {t('buttons.addRegistryFuelCard')}
                    </Button>
                }
            </Stack>
        )
    }

    const deleteRegistryFuelCardRequest = () => {
        deleteRegistryFuelCard(rowId).then(res => {
                if (res.status === "OK") {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t("messages.deleteSuccess"),
                                type: snackbarTypes.success,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                    setUpdating(true);
                } else {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t(`snackBarErrors.${res.message}`),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                }
            }
        );
    }

    useEffect(() => {
        if (!openDetailModal) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(true);
        }
    }, [openDetailModal]);



    return (
        <>
            <AgGridCustomTable
                rowModelType="serverSide"
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                noRowsOverlayComponent={noRowsOverlayComponent}
                suppressRowClickSelection
                toolbar={toolbar}
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));
                }}
                onColumnMoved={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnPinned={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnResized={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onSortChanged={e => {
                    if (e.source === "api") return;
                    saveState();
                }} >                
            </AgGridCustomTable>
            <RegistryFuelCardDetail open={openDetailModal} setOpen={setOpenDetailModal}
                                    registryFuelCardObj={registryFuelCard} isReadonly={isReadonly}
                                    copyRegistryCard={copyRegistryCard}/>
            <FinanceDialog
                title={t(`modalDelete.title`)}
                content={t(`modalDelete.content`, {cardNumber: cardNumber})}
                cancelButton={t('modalDelete.buttons.cancel')}
                open={openDeleteDialog}
                onClose={() => {
                    setOpenDeleteDialog(false);
                }}
                confirmButton={t('modalDelete.buttons.deleteRegistryFuelCard')}
                callback={() => {
                    deleteRegistryFuelCardRequest();
                    setOpenDeleteDialog(false);
                }}
                titleIcon={<WarningRounded/>}
            />
        </>

    );
}

export default RegistryFuelCardsTable;